import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useMemo, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Label } from '../Forms/Label/Label';
import Spinner from '../Spinner';
import AddButton from './AddButton';
import List from './List';
import Tag from './Tag';
import { TagOption } from './TagOption';
import styles from './TagsSelector.module.scss';

interface TagsSelectorProps<T> {
    title?: string;
    tags: Array<TagOption<T>>;
    selected: Array<TagOption<T>>;
    selectAllLabel?: string;
    selectAllIcon?: IconDefinition;
    loading?: boolean;
    searchPlaceholder?: string;
    addButtonColor?: string;
    direction?: 'up' | 'down';
    disabled?: boolean;
    className?: string;
    onSelect?: (id: T) => void;
    onSelectAll?: (ids: Array<T>) => void;
    onRemove?: (id: T) => void;
}

export const TagsSelector = <T,>(props: TagsSelectorProps<T>) => {
    const triggerRef = useRef<HTMLDivElement>(null);

    const [expanded, setExpanded] = useState(false);

    const unselectedTags = useMemo(
        () => props.tags.filter(tag => !props.selected.some(selectedTag => selectedTag.id === tag.id)),
        [props.tags, props.selected]
    );

    const selectHandler = (id: T) => {
        setExpanded(false);
        props.onSelect?.(id);
    };

    const selectAllHandler = (ids: Array<T>) => {
        setExpanded(false);
        props.onSelectAll?.(ids);
    };

    return (
        <div className={clsx(styles.container, props.className)}>
            {!isEmpty(props.title) && <Label className={styles.title}>{props.title}</Label>}
            {props.loading ? (
                <Spinner size={30} />
            ) : (
                <div className={styles.tagsContainer}>
                    {props.selected.map(tag => (
                        <Tag
                            key={String(tag.id)}
                            tag={tag}
                            disabled={props.disabled}
                            onRemove={() => props.onRemove?.(tag.id)}
                        />
                    ))}
                    {props.tags.length > props.selected.length && !props.disabled && (
                        <div ref={triggerRef}>
                            <AddButton
                                color={props.addButtonColor}
                                onClick={() => setExpanded(true)}
                            />
                        </div>
                    )}
                    {expanded && (
                        <ClickAwayListener onClickAway={() => setExpanded(false)}>
                            <div>
                                <List
                                    triggerRef={triggerRef}
                                    searchable={true}
                                    searchPlaceholder={props.searchPlaceholder}
                                    tags={unselectedTags}
                                    selectAllLabel={props.selectAllLabel}
                                    selectAllIcon={props.selectAllIcon}
                                    direction={props.direction}
                                    onClose={() => setExpanded(false)}
                                    onSelect={selectHandler}
                                    onSelectAll={props.onSelectAll ? selectAllHandler : undefined}
                                />
                            </div>
                        </ClickAwayListener>
                    )}
                </div>
            )}
        </div>
    );
};

export default TagsSelector;
